import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WcIcon from '@mui/icons-material/Wc';
import PublicIcon from '@mui/icons-material/Public';
import HeightIcon from '@mui/icons-material/Height';
import ScaleIcon from '@mui/icons-material/Scale';
import FavoriteIcon from '@mui/icons-material/Favorite';

export default function BasicDataInputSection({ state, handlers }) {
  const {
    age,
    gender,
    ethnicity,
    height,
    weight,
    bmi,
    systolic,
    diastolic,
    bpDate,
  } = state;

  const {
    onAgeChange,
    onGenderChange,
    onEthnicityChange,
    onHeightChange,
    onWeightChange,
    onBmiChange,
    onSystolicChange,
    onDiastolicChange,
    onBpDateChange,
    onBpFileUpload,
  } = handlers;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file?.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (e) => onBpFileUpload(e.target.result);
      reader.readAsText(file);
    }
  };

  const handleTemplateDownload = () => {
    const template = '120/80 - 2025-01-01\n130/85 - 2025-03-10';
    const blob = new Blob([template], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blood_pressure_template.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card sx={{ mb: 6 }}>
      <CardHeader title="Patient Basic Data" sx={{ backgroundColor: '#f5f5f5' }} />
      <CardContent>
        <Grid container spacing={4}>
          {/* Age */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarTodayIcon sx={{ mr: 1 }} /> Age
            </Typography>
            <TextField
              label="Age"
              type="number"
              fullWidth
              value={age}
              onChange={(e) => onAgeChange(e.target.value)}
            />
          </Grid>

          {/* Gender */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
              <WcIcon sx={{ mr: 1 }} /> Gender
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select
                labelId="gender-label"
                value={gender}
                label="Gender"
                onChange={(e) => onGenderChange(e.target.value)}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Ethnicity */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
              <PublicIcon sx={{ mr: 1 }} /> Ethnicity
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="ethnicity-label">Ethnicity</InputLabel>
              <Select
                labelId="ethnicity-label"
                value={ethnicity}
                label="Ethnicity"
                onChange={(e) => onEthnicityChange(e.target.value)}
              >
                <MenuItem value="hispanic">Hispanic or Latino</MenuItem>
                <MenuItem value="non-hispanic">Non-Hispanic or Latino</MenuItem>
                <MenuItem value="unknown">Unknown</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Height and Weight */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
              <HeightIcon sx={{ mr: 1 }} /> Height and Weight
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Height (ft)"
                  type="number"
                  fullWidth
                  value={height}
                  onChange={(e) => onHeightChange(e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Weight (lbs)"
                  type="number"
                  fullWidth
                  value={weight}
                  onChange={(e) => onWeightChange(e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* BMI */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
              <ScaleIcon sx={{ mr: 1 }} /> BMI
            </Typography>
            <TextField
              label="BMI"
              type="number"
              fullWidth
              value={bmi}
              onChange={(e) => onBmiChange(e.target.value)}
            />
          </Grid>

          {/* Blood Pressure */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
              <FavoriteIcon sx={{ mr: 1 }} /> Blood Pressure
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Systolic"
                  type="number"
                  fullWidth
                  value={systolic}
                  onChange={(e) => onSystolicChange(e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Diastolic"
                  type="number"
                  fullWidth
                  value={diastolic}
                  onChange={(e) => onDiastolicChange(e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={bpDate}
                  onChange={(e) => onBpDateChange(e.target.value)}
                />
              </Grid>
            </Grid>

            <Box mt={2} display="flex" gap={2}>
              <Button variant="outlined" component="label">
                Upload .txt file
                <input type="file" hidden onChange={handleFileChange} accept=".txt" />
              </Button>
              <Button variant="text" onClick={handleTemplateDownload}>
                Download .txt template
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
